import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import {
  deletePlateRegistration,
  downloadRegistrationExport,
  previewRegistrationExport,
  uploadPlateRegistration
} from '../requests/plateRegistration.request';
import {
  RegistrationExportPreview,
  RegistrationExportRequest
} from '../types/plateRegistration.types';
import { clientRegistrationSummaryKey } from '../queries/plateRegistration.query';

export const useUploadPlateRegistrationMutation = (clientId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ plateId, file }: { plateId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      return uploadPlateRegistration(plateId, formData);
    },
    onError: (error: AxiosError) => {
      toast.error(error.message || 'Could not upload the registration');
    },
    onSuccess: (_data, variables) => {
      toast.success('Registration uploaded');
      queryClient.invalidateQueries({ queryKey: clientRegistrationSummaryKey(clientId) });
      queryClient.invalidateQueries({
        queryKey: ['plate-registration-history', variables.plateId]
      });
    }
  });
};

export const useDeletePlateRegistrationMutation = (clientId?: string, plateId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileId: string) => deletePlateRegistration(fileId),
    onError: (error: AxiosError) => {
      toast.error(error.message || 'Could not delete the registration');
    },
    onSuccess: () => {
      toast.success('Registration deleted');
      queryClient.invalidateQueries({ queryKey: clientRegistrationSummaryKey(clientId) });
      queryClient.invalidateQueries({ queryKey: ['plate-registration-history', plateId] });
    }
  });
};

export const usePreviewRegistrationExportMutation = () => {
  return useMutation<RegistrationExportPreview | undefined, AxiosError, RegistrationExportRequest>({
    mutationKey: ['registration-export-preview'],
    mutationFn: previewRegistrationExport,
    onError: (error) => {
      toast.error(error.message || 'Could not build the export preview');
    }
  });
};

export const useDownloadRegistrationExportMutation = () => {
  return useMutation({
    mutationKey: ['registration-export-download'],
    mutationFn: ({
      payload,
      chunkIndex,
      fallbackFileName
    }: {
      payload: RegistrationExportRequest;
      chunkIndex: number;
      fallbackFileName: string;
    }) => downloadRegistrationExport(payload, chunkIndex, fallbackFileName),
    onError: (error: AxiosError) => {
      toast.error(error.message || 'Could not download the export');
    },
    onSuccess: (downloaded) => {
      if (!downloaded) toast.error('No registrations matched those filters');
    }
  });
};
